import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PiPaperclipLight, PiChatCircleTextLight, PiCalendarLight } from 'react-icons/pi';
import Input from '../shared/TailwindComponents/Input';
import Textarea from '../shared/TailwindComponents/Textarea';
import Button from '../shared/TailwindComponents/Button';
import CheckBox from '../shared/TailwindComponents/CheckBox';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

interface TaskItem {
  TaskTitle: string;
  TaskDescription: string;
  TaskDeadline: string;
  MatterID?: string;
  CaseID?: string;
  ProjectID?: string;
  TeamID?: string;
  selected?: boolean;
}

interface SuggestedTasksModalProps {
  tasks: TaskItem[];
  onConfirm?: (selectedTasks: TaskItem[]) => void;
  onCancel?: () => void;
  caseId?: string;
  matterUuid?: string;
}

const SuggestedTasksModal: React.FC<SuggestedTasksModalProps> = ({ tasks, onConfirm, onCancel, caseId, matterUuid }) => {
  const { t } = useTranslation();
  const [taskList, setTaskList] = useState<TaskItem[]>(
    tasks.map(task => ({ ...task, selected: true }))
  );

  const handleChange = (index: number, field: keyof TaskItem, value: string | boolean) => {
    const newList = [...taskList];
    if (field === 'selected') {
      newList[index][field] = value as boolean;
    } else {
      newList[index][field] = value as string;
    }
    setTaskList(newList);
  };

  const toggleSelect = (index: number) => {
    handleChange(index, 'selected', !taskList[index].selected);
  };

  const addNewTask = () => {
    setTaskList(prev => [
      ...prev,
      {
        TaskTitle: '',
        TaskDescription: '',
        TaskDeadline: '',
        MatterID: matterUuid || caseId,
        CaseID: caseId,
        selected: true
      }
    ]);
  };

  const handleConfirm = () => {
    const selectedTasks = taskList
      .filter(task => task.selected)
      .map(({ selected, ...rest }) => ({
        ...rest,
        MatterID: rest.MatterID || matterUuid || caseId,
        CaseID: rest.CaseID || caseId
      }));
    onConfirm && onConfirm(selectedTasks);
    onCancel && onCancel();
  };

  return (
    <div className="space-y-4 md:w-[900px]">
      <div className="max-h-[60vh] overflow-y-auto pe-2 custom-scrollbar-main px-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {taskList.map((task, index) => (
            <div
              key={index}
              className="glass relative rounded-lg p-4 shadow-sm hover:shadow-md transition-all duration-300 hover:bg-gray-50 dark:hover:bg-gray-700 flex flex-col gap-3"
            >
              <div className="flex items-center justify-between">
                <Input
                  type="text"
                  value={task.TaskTitle}
                  onChange={(e) => handleChange(index, 'TaskTitle', e.target.value)}
                  placeholder={t('Enter task title')}
                  className="text-lg text-gray-900 dark:text-white border-none p-0 focus:ring-0"
                />
              </div>

              <Textarea
                id={`description-${index}`}
                name={`description-${index}`}
                value={task.TaskDescription}
                onChange={(e) => handleChange(index, 'TaskDescription', e.target.value)}
                placeholder={t('Enter task description')}
                className="text-sm text-gray-600 dark:text-gray-400 border-gray-300 dark:border-gray-600 rounded-md"
                rows={3}
                label=""
              />

              <div className="flex items-center gap-2">
                <PiCalendarLight className="h-5 w-5 text-gray-500" />
                <DatePicker
                  selected={task.TaskDeadline ? new Date(task.TaskDeadline) : null}
                  onChange={(date: Date) => handleChange(index, 'TaskDeadline', date ? date.toISOString() : '')}
                  showTimeSelect
                  timeFormat="HH:mm"
                  timeIntervals={15}
                  dateFormat="MMM d, yyyy h:mm aa"
                  className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500"
                  placeholderText={t('Select date and time')}
                />
              </div>

              <div className="flex justify-between items-center">
                <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400">
                  <div className="flex items-center gap-1">
                    <PiPaperclipLight />
                    <span>0</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <PiChatCircleTextLight />
                    <span>0</span> 
                  </div>
                </div>
                <div className="flex -space-x-2">
                  <CheckBox
                    label={t('Select task')}
                    hideLabel
                    translate={false}
                    checked={!!task.selected}
                    onChange={() => toggleSelect(index)}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-between items-center mt-6 pt-6 pb-4 px-4 border-1 border-t border-gray-300">
        <Button
          variant="tertiary"
          size="small"
          onClick={addNewTask}
          className="flex items-center gap-2"
        >
          {t('Add Another Task')}
        </Button>
        <div className="flex justify-end space-x-2">
          <Button variant="neutral" size="small" onClick={onCancel}>
            {t('Cancel')}
          </Button>
          <Button variant="primary" size="small" onClick={handleConfirm}>
            {t('Generate Tasks')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SuggestedTasksModal;
